import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { useSettings } from '@/contexts/SettingsContext';
import { updateSiteSettings } from '@/services/api';
import { publicAnonKey } from '/utils/supabase/info';

export function Settings() {
  const { settings, refreshSettings } = useSettings();
  const [localSettings, setLocalSettings] = useState(settings);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');

    const result = await updateSiteSettings(localSettings, publicAnonKey);

    if (result) {
      setSaveMessage('Beállítások sikeresen mentve!');
      await refreshSettings();
      setTimeout(() => setSaveMessage(''), 3000);
    } else {
      setSaveMessage('Hiba történt a mentés során.');
    }

    setIsSaving(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Beállítások</h2>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white hover:bg-gray-800 disabled:bg-gray-400 transition-colors"
        >
          <Save size={20} />
          {isSaving ? 'Mentés...' : 'Mentés'}
        </button>
      </div>

      {saveMessage && (
        <div
          className={`p-4 ${
            saveMessage.includes('sikeres') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}
        >
          {saveMessage}
        </div>
      )}

      <div className="bg-white border border-gray-200 p-6 space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Menüpontok láthatósága</h3>

          {/* Admin Menu Visibility */}
          <div className="flex items-center justify-between p-4 border border-gray-200 bg-gray-50">
            <div>
              <p className="font-medium">Admin menüpont</p>
              <p className="text-sm text-gray-600 mt-1">
                Ha bekapcsolva: az Admin menüpont mindig látható<br />
                Ha kikapcsolva: csak bejelentkezés után jelenik meg
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={localSettings.adminMenuVisible}
                onChange={(e) =>
                  setLocalSettings({ ...localSettings, adminMenuVisible: e.target.checked })
                }
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-black rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
            </label>
          </div>

          {/* News Enabled */}
          <div className="flex items-center justify-between p-4 border border-gray-200 bg-gray-50">
            <div>
              <p className="font-medium">Hírek menüpont</p>
              <p className="text-sm text-gray-600 mt-1">
                Ha bekapcsolva: a Hírek menüpont megjelenik a navigációban<br />
                Ha kikapcsolva: a Hírek menüpont rejtve marad
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={localSettings.newsEnabled}
                onChange={(e) =>
                  setLocalSettings({ ...localSettings, newsEnabled: e.target.checked })
                }
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-black rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
            </label>
          </div>

          {/* English Enabled */}
          <div className="flex items-center justify-between p-4 border border-gray-200 bg-gray-50">
            <div>
              <p className="font-medium">Angol tartalom</p>
              <p className="text-sm text-gray-600 mt-1">
                Ha bekapcsolva: nyelvválasztó megjelenik, mindkét nyelv elérhető<br />
                Ha kikapcsolva: csak magyar nyelv, a menü a lap jobb szélére igazítva
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={localSettings.englishEnabled}
                onChange={(e) =>
                  setLocalSettings({ ...localSettings, englishEnabled: e.target.checked })
                }
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-black rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}